import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import CurrencyInput, { formatPrice, getCurrencySymbol } from '../components/CurrencyInput';
import { Plus, Edit, Trash2, X, ArrowLeft, Copy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageServices = () => {
  const { token, user, isAdmin, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalError, setModalError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  
  const [formData, setFormData] = useState({
    service_name: '',
    service_price: '',
    service_currency: null, // Will be auto-detected
    service_duration: '60',
    customers_allowed: 1,
    service_description: '',
    color_code: '#3B82F6',
    calendar_ids: []
  });

  const predefinedColors = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Yellow', value: '#EAB308' },
    { name: 'Teal', value: '#14B8A6' }
  ];

  const fetchPlanFeatures = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me/plan-usage`, {
        headers: getAuthHeaders()
      });
      if (response.data?.data?.features) {
        setPlanFeatures(response.data.data.features);
      }
    } catch (err) {
      console.error('Error fetching plan features:', err);
    }
  }, [token]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const [servicesRes, calendarsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/services`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_BASE_URL}/calendars`, {
          headers: getAuthHeaders()
        })
      ]);
      setServices(servicesRes.data.data || []);
      setCalendars(calendarsRes.data.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = err.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        setError('Failed to load services. Please try again.');
      } else {
        setError(errorMessage || 'Failed to load services');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchServices();
    fetchPlanFeatures();
    
    // Check for success message from location state
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from history state
      window.history.replaceState({}, document.title);
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    }
  }, [isAdmin, navigate, fetchServices, fetchPlanFeatures, location.state]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleColorSelect = (color) => {
    setFormData({ ...formData, color_code: color });
  };

  const openAddModal = () => {
    // Check if user can add more services based on plan (using service_limit)
    const serviceLimit = planFeatures?.service_limit ?? 1;
    const isUnlimited = serviceLimit === -1;
    
    if (!isUnlimited && services.length >= serviceLimit) {
      setError(`Your current plan only allows ${serviceLimit} service(s). Please upgrade to add more services.`);
      setTimeout(() => setError(''), 5000);
      return;
    }
    
    setEditingService(null);
    setModalError('');
    setFormData({
      service_name: '',
      service_price: '',
      service_currency: null, // Will be auto-detected by CurrencyInput
      service_duration: '60',
      customers_allowed: 1,
      service_description: '',
      color_code: '#3B82F6',
      calendar_ids: []
    });
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setModalError('');
    
    // Find which calendars this service is assigned to
    const assignedCalendarIds = calendars
      .filter(cal => cal.service_ids && cal.service_ids.includes(service.id))
      .map(cal => cal.id);
    
    setFormData({
      service_name: service.service_name,
      service_price: service.service_price,
      service_currency: service.service_currency || 'HKD', // Default to HKD for existing services
      service_duration: String(service.service_duration),
      customers_allowed: service.customers_allowed,
      service_description: service.service_description || '',
      color_code: service.color_code || '#3B82F6',
      calendar_ids: assignedCalendarIds
    });
    setShowModal(true);
  };

  const openCopyModal = (service) => {
    setEditingService(null); // null means creating new
    setModalError('');
    setFormData({
      service_name: `${service.service_name} (Copy)`,
      service_price: service.service_price,
      service_currency: service.service_currency || 'HKD',
      service_duration: String(service.service_duration),
      customers_allowed: service.customers_allowed,
      service_description: service.service_description || '',
      color_code: service.color_code || '#3B82F6',
      calendar_ids: [] // Don't copy calendar assignments
    });
    setShowModal(true);
  };

  const handleCalendarToggle = (calendarId) => {
    // Check if trying to add to a Fixed calendar with non-60 minute duration
    const calendar = calendars.find(c => c.id === calendarId);
    const isCurrentlySelected = formData.calendar_ids?.includes(calendarId);
    
    // If trying to SELECT a Fixed calendar and duration is not 60
    if (!isCurrentlySelected && calendar?.booking_type === 'fixed' && formData.service_duration !== '60') {
      setError(`Cannot add to "${calendar.name}" (Fixed calendar) - service duration must be 60 minutes. Current duration: ${formData.service_duration} minutes.`);
      return; // Don't toggle
    }
    
    setFormData(prev => {
      const currentIds = prev.calendar_ids || [];
      let newCalendarIds;
      
      if (currentIds.includes(calendarId)) {
        newCalendarIds = currentIds.filter(id => id !== calendarId);
      } else {
        newCalendarIds = [...currentIds, calendarId];
      }
      
      // Check if any selected calendar is "fixed" type
      const hasFixedCalendar = newCalendarIds.some(id => {
        const cal = calendars.find(c => c.id === id);
        return cal?.booking_type === 'fixed';
      });
      
      // If any fixed calendar is selected, force duration to 60
      if (hasFixedCalendar) {
        return { ...prev, calendar_ids: newCalendarIds, service_duration: '60' };
      }
      
      return { ...prev, calendar_ids: newCalendarIds };
    });
    
    // Clear error when successfully toggling
    setError('');
  };

  // Check if any selected calendar is "fixed" type (duration must be 60)
  const hasFixedCalendarSelected = () => {
    if (!formData.calendar_ids || formData.calendar_ids.length === 0) return false;
    return formData.calendar_ids.some(id => {
      const calendar = calendars.find(c => c.id === id);
      return calendar?.booking_type === 'fixed';
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted!', formData);
    setError('');
    setModalError('');
    setSaving(true);

    // Validate form
    if (!formData.service_name || !formData.service_price || !formData.service_duration || !formData.customers_allowed) {
      console.log('Validation failed:', formData);
      setModalError('Please fill in all required fields');
      setSaving(false);
      return;
    }

    console.log('Making API call');
    try {
      const serviceData = {
        service_name: formData.service_name,
        service_price: parseFloat(formData.service_price),
        service_currency: formData.service_currency || 'HKD',
        service_duration: parseInt(formData.service_duration),
        customers_allowed: parseInt(formData.customers_allowed),
        service_description: formData.service_description,
        color_code: formData.color_code,
        calendar_ids: formData.calendar_ids || []
      };

      if (editingService) {
        // Update existing service
        await axios.put(
          `${API_BASE_URL}/services/${editingService.id}`,
          serviceData,
          { headers: getAuthHeaders() }
        );
        setSuccess('Service updated successfully!');
      } else {
        // Create new service
        await axios.post(
          `${API_BASE_URL}/services`,
          serviceData,
          { headers: getAuthHeaders() }
        );
        setSuccess('Service added successfully!');
      }

      setShowModal(false);
      await fetchServices();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving service:', err);
      const errorMessage = err.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        setModalError('Please check your input and try again.');
      } else {
        setModalError(errorMessage || 'Failed to save service');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (serviceId) => {
    // Navigate to delete confirmation page
    navigate(`/admin/services/delete/${serviceId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Manage Services</h1>
              <p className="text-gray-600 mt-2">Create and manage your service offerings</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {(() => {
                // Calculate if user can add more services based on service_limit
                // service_limit: -1 means unlimited, otherwise it's the max number
                const serviceLimit = planFeatures?.service_limit ?? 1;
                const isUnlimited = serviceLimit === -1;
                const canAddService = isUnlimited || services.length < serviceLimit;
                
                return (
                  <>
                    <button
                      onClick={openAddModal}
                      disabled={!canAddService}
                      className={`font-semibold px-6 py-3 rounded-md transition-colors flex items-center ${
                        !canAddService
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      }`}
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Add Service
                    </button>
                    {!canAddService && (
                      <p className="text-xs text-orange-600">
                        ⚡ <a href="/pricing" className="underline">Upgrade to add more services</a> (Limit: {serviceLimit})
                      </p>
                    )}
                    {canAddService && !isUnlimited && (
                      <p className="text-xs text-gray-500">
                        {services.length} / {serviceLimit} services used
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {services.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500 text-lg mb-4">No services added yet</p>
              <button
                onClick={openAddModal}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-2 rounded-md transition-colors"
              >
                Add Your First Service
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Max Customers
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {services.map((service) => (
                    <tr key={service.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-4 h-4 rounded-full mr-3"
                            style={{ backgroundColor: service.color_code }}
                          ></div>
                          <span className="text-sm font-medium text-gray-900">
                            {service.service_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatPrice(service.service_price, service.service_currency)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {service.service_duration} min
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {service.customers_allowed}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500 line-clamp-2">
                          {service.service_description || 'No description'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openCopyModal(service)}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Copy Service"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openEditModal(service)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Edit Service"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Service"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {modalError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {modalError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    name="service_name"
                    value={formData.service_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="e.g., Haircut, Massage, Personal Training"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price *
                    </label>
                    <CurrencyInput
                      value={formData.service_price}
                      currency={formData.service_currency}
                      onValueChange={(val) => setFormData({ ...formData, service_price: val })}
                      onCurrencyChange={(code) => setFormData({ ...formData, service_currency: code })}
                      placeholder="25.00"
                      required
                      data-testid="service-price"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration (minutes) *
                    </label>
                    <input
                      type="number"
                      name="service_duration"
                      value={formData.service_duration}
                      onChange={handleChange}
                      min="1"
                      disabled={hasFixedCalendarSelected()}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                        hasFixedCalendarSelected() ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                      }`}
                      placeholder="60"
                      required
                    />
                    {hasFixedCalendarSelected() && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Fixed calendars require 60-minute duration (top-of-hour booking slots)
                      </p>
                    )}
                    {!hasFixedCalendarSelected() && formData.service_duration && formData.service_duration !== '60' && (
                      <p className="text-xs text-orange-500 mt-1">
                        ⚠️ This service cannot be added to Fixed calendars (requires 60-minute duration)
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Customers Allowed *
                  </label>
                  <input
                    type="number"
                    name="customers_allowed"
                    value={formData.customers_allowed}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Code
                  </label>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {predefinedColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => handleColorSelect(color.value)}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          formData.color_code === color.value
                            ? 'border-gray-800 scale-110'
                            : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <input
                    type="color"
                    name="color_code"
                    value={formData.color_code}
                    onChange={handleChange}
                    className="w-full h-12 px-2 border border-gray-300 rounded-md cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="service_description"
                    value={formData.service_description}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Describe your service..."
                  />
                </div>

                {/* Calendar Assignment - Only show if calendars exist */}
                {calendars.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add to Calendar(s)
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Select which calendar(s) this service should be available on
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                      {calendars.map((calendar) => {
                        const isFixedCalendar = calendar.booking_type === 'fixed';
                        const durationIsNot60 = formData.service_duration !== '60';
                        // Disable fixed calendar selection only when service duration is not 60 minutes
                        const isDisabled = isFixedCalendar && durationIsNot60;
                        
                        return (
                          <label 
                            key={calendar.id} 
                            className={`flex items-center p-2 rounded ${
                              isDisabled 
                                ? 'bg-gray-100 cursor-not-allowed opacity-60' 
                                : 'hover:bg-white cursor-pointer'
                            }`}
                            title={isDisabled ? 'Fixed calendars require 60-minute service duration' : ''}
                          >
                            <input
                              type="checkbox"
                              checked={formData.calendar_ids?.includes(calendar.id) || false}
                              onChange={() => handleCalendarToggle(calendar.id)}
                              disabled={isDisabled}
                              className={`w-4 h-4 border-gray-300 rounded focus:ring-yellow-500 ${
                                isDisabled ? 'cursor-not-allowed' : 'text-yellow-500'
                              }`}
                            />
                            <span className={`ml-3 text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                              {calendar.name}
                            </span>
                            {isFixedCalendar && (
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                isDisabled ? 'bg-gray-200 text-gray-500' : 'bg-amber-100 text-amber-700'
                              }`}>
                                Fixed {isDisabled && '(60 min only)'}
                              </span>
                            )}
                            {calendar.booking_type === 'floating' && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Floating</span>
                            )}
                            {calendar.type && (
                              <span className="ml-2 text-xs text-gray-400">({calendar.type})</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {formData.calendar_ids?.length > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        ✓ Selected {formData.calendar_ids.length} calendar(s)
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      editingService ? 'Update Service' : 'Add Service'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageServices;
